/**
 * License Document Types
 *
 * Defines the structure for license documents stored in:
 * /organizations/{artistOrgId}/audiometa/{trackId}/licenses/{licenseId}
 */

// License status options
export type LicenseStatus = 'active' | 'expired' | 'pending' | 'inactive';

// License duration type values (matching existing LICENSE_DURATIONS)
export type LicenseDurationType =
  | 'none'
  | '3months'
  | '6months'
  | '12months'
  | '2years'
  | '3years'
  | '5years'
  | 'blanket'
  | 'custom';

// Standard field format (matches existing collection field format)
export interface LicenseField {
  label: string;
  value: string;
}

// Main license document interface
export interface LicenseDocument {
  // Document ID (auto-generated by Firestore)
  id?: string;

  // ===== CORE LINKING FIELDS =====
  /** Target org ID - null if not shared or pending claim */
  clientOrgId: string | null;
  /** Artist org that created the license (for querying) */
  sourceOrgId: string;
  /** Unique code for unclaimed licenses (when shared but recipient has no org) */
  claimCode: string | null;

  // ===== SHARING METADATA =====
  /** When the license was created (milliseconds since epoch) */
  createdAt: number;
  /** User ID who created the license */
  createdBy: string;
  /** When the license was shared (milliseconds since epoch) - null if not shared */
  sharedAt: number | null;
  /** User ID who shared the license - null if not shared */
  sharedBy: string | null;
  /** License status */
  status: LicenseStatus;

  // ===== LICENSE-SPECIFIC METADATA =====
  /** Campaign/project name */
  jobTitle: string;
  /** From global /client collection */
  client: LicenseField | null;
  /** From global /agency collection */
  agency: LicenseField | null;
  /** From org /producer collection */
  producer: LicenseField | null;
  /** Array of creative directors */
  creativeDirectors: LicenseField[];
  /** From global /flightingplatform collection */
  flightingPlatform: LicenseField[];
  /** From global /territories collection */
  territories: LicenseField[];

  // ===== DATE FIELDS =====
  /** License start date (milliseconds since epoch) */
  dateStart: number | null;
  /** License expiry date (milliseconds since epoch) - null for Blanket licenses */
  dateExpires: number | null;

  // ===== LICENSE TYPE =====
  /** Duration type selected */
  licenseType: LicenseDurationType;

  // ===== EXCLUSIVITY =====
  /** Whether this is an exclusive license (only one active at a time) */
  isExclusive?: boolean;

  // ===== OPTIONAL FIELDS =====
  /** Additional notes */
  notes?: string;
}

// License creation input (without auto-generated fields)
export interface CreateLicenseInput {
  sourceOrgId: string;
  jobTitle?: string;
  client?: LicenseField | null;
  agency?: LicenseField | null;
  producer?: LicenseField | null;
  creativeDirectors?: LicenseField[];
  flightingPlatform?: LicenseField[];
  territories?: LicenseField[];
  dateStart?: number | null;
  dateExpires?: number | null;
  licenseType: LicenseDurationType;
  notes?: string;
  // Optional sharing info
  clientOrgId?: string | null;
  generateClaimCode?: boolean;
}

// License update input
export interface UpdateLicenseInput {
  jobTitle?: string;
  client?: LicenseField | null;
  agency?: LicenseField | null;
  producer?: LicenseField | null;
  creativeDirectors?: LicenseField[];
  flightingPlatform?: LicenseField[];
  territories?: LicenseField[];
  dateStart?: number | null;
  dateExpires?: number | null;
  licenseType?: LicenseDurationType;
  notes?: string;
  status?: LicenseStatus;
}

// Share license input
export interface ShareLicenseInput {
  clientOrgId?: string | null;
  generateClaimCode?: boolean;
}

// Helper function to generate a unique claim code
export function generateClaimCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed ambiguous chars (0, O, 1, I)
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// Helper function to determine license status based on dates
export function calculateLicenseStatus(
  license: Pick<LicenseDocument, 'dateExpires' | 'clientOrgId' | 'claimCode' | 'status'>
): LicenseStatus {
  // If has claim code but no clientOrgId, it's pending
  if (license.claimCode && !license.clientOrgId) {
    return 'pending';
  }

  // If no expiry date (blanket license), check if shared
  if (license.dateExpires === null) {
    return 'active';
  }

  // Check if expired
  const now = Date.now();
  if (license.dateExpires < now) {
    return 'expired';
  }

  return 'active';
}

// Helper to create a default/empty license document
export function createDefaultLicense(sourceOrgId: string, createdBy: string): LicenseDocument {
  return {
    clientOrgId: null,
    sourceOrgId,
    claimCode: null,
    createdAt: Date.now(),
    createdBy,
    sharedAt: null,
    sharedBy: null,
    status: 'active',
    jobTitle: '',
    client: null,
    agency: null,
    producer: null,
    creativeDirectors: [],
    flightingPlatform: [],
    territories: [],
    dateStart: Date.now(),
    dateExpires: null,
    licenseType: 'none',
    notes: '',
  };
}
